import React, { useState } from 'react';
import { Card, message, Tooltip, Modal, Input, Button } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import TaskDetails from './TaskDetails';
import { Kanban } from '../Kanban/Kanban.jsx';
import { useNavigate, Outlet } from 'react-router-dom';

const { TextArea } = Input;

const TaskKanban = ({
  tasks,
  refreshFlag,
  onTaskUpdated,
  onStatusChange,
  onDelete,
  onEdit,
  onView,
  candidates,
  users,
  STATUSES,
  selectedStatuses = []
}) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeResult, setCompleteResult] = useState('');
  const [pendingTask, setPendingTask] = useState(null);

  const navigate = useNavigate();

  const getEntityName = (id, entities) => {
    const entity = entities.find(e => e.id === id);
    return entity ? entity.full_name || `${entity.first_name} ${entity.last_name}` : 'Не указан';
  };

  const getVacancyName = (task) => {
    if (task.vacancy_name) {
      return task.vacancy_name;
    }

    if (task.vacancy && task.vacancy.job_title) {
      return task.vacancy.job_title;
    }

    if (!task.vacancy_id) return 'Не указано';

    const candidate = candidates.find(c => c.id === task.candidate_id);
    if (!candidate || !candidate.vacancies || !Array.isArray(candidate.vacancies)) {
      return 'Не указано';
    }

    const vacancy = candidate.vacancies.find(v => v.id === task.vacancy_id);
    return vacancy ? vacancy.job_title : 'Не указано';
  };

  const isTransitionAllowed = (fromStatus, toStatus) => {
    if (fromStatus === 'new') {
      return toStatus === 'in_progress';
    }

    if (fromStatus === 'closed') {
      return toStatus === 'in_progress';
    }

    if (fromStatus === 'in_progress') {
      return toStatus === 'in_progress' || toStatus === 'closed' || toStatus === 'new';
    }

    return false;
  };

  const getAllowedStatuses = (fromStatus) => {
    if (fromStatus === 'new') {
      return ['in_progress'];
    }
    if (fromStatus === 'closed') {
      return ['in_progress'];
    }
    if (fromStatus === 'in_progress') {
      return ['new', 'in_progress', 'closed'];
    }
    return [];
  };

  const handleDelete = async (taskId) => {
    try {
      await onDelete(taskId);
      message.success('Задача удалена');
      onTaskUpdated();
    } catch (error) {
      message.error('Ошибка при удалении задачи');
    }
  };

  const handleViewDetails = (task) => {
    navigate(`/tasks/${task.id}`);
    setSelectedTask(task);
    setDetailsVisible(true);
  };

  const handleCompleteTaskSubmit = async () => {
    if (!pendingTask) return;

    // Если result_required = true, проверяем что результат заполнен
    if (pendingTask.item.result_required && !completeResult.trim()) {
      message.warning('Необходимо указать результат выполнения задачи');
      return;
    }

    try {
      await onStatusChange(pendingTask.item, pendingTask.newStatus, pendingTask.newIndex);
      setShowCompleteModal(false);
      setCompleteResult('');
      setPendingTask(null);
      message.success('Задача завершена');
    } catch (error) {
      message.error('Ошибка при завершении задачи');
    }
  };

  const handleCompleteTaskCancel = () => {
    setShowCompleteModal(false);
    setCompleteResult('');
    setPendingTask(null);
    setDraggedTask(null);
  };


  const renderCard = (task) => {
    const currentUserId = parseInt(localStorage.getItem('user_id'));
    const canEdit = currentUserId === task.creator_id;

    return (
      <Card
        key={task.id}
        size="small"
        style={{ marginBottom: '8px' }}
        actions={[
          <Tooltip title="Просмотреть задачу">
            <EyeOutlined key="view" onClick={() => navigate(`/tasks/${task.id}`)} />
          </Tooltip>,
          <Tooltip title={canEdit ? "Редактировать задачу" : "Только автор может редактировать задачу"}>
            <EditOutlined
              key="edit"
              onClick={() => onEdit(task)}
              style={{
                color: canEdit ? '#1890ff' : '#d9d9d9',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                opacity: canEdit ? 1 : 0.5
              }}
            />
          </Tooltip>,
          // <Tooltip title="Удалить задачу">
          //   <DeleteOutlined key="delete" onClick={() => handleDelete(task.id)} />
          // </Tooltip>,
        ]}
      >
        <p>ID задачи: {task.id}</p>
        <p>ФИО кандидата: {getEntityName(task.candidate_id, candidates)}</p>
        <p>Вакансия: {getVacancyName(task)}</p>
        <p>Описание: {task.description}</p>
        <p>Срок: {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'Не указан'}</p>
        <p>Исполнитель: {getEntityName(task.assignee_id, users)}</p>
        <p>Требуется результат: {task.result_required ? 'Да' : 'Нет'}</p>

      </Card>
    );
  };

  const handleDragStart = async (item) => {
    try {
      setDraggedTask(item);
    } catch (error) {
      // Обработка ошибки при начале перетаскивания
    }
  };

  const handleDragEnd = async (item, newStatus, newIndex) => {
    try {
      // Получаем оригинальный статус из состояния tasks
      const originalTask = tasks.find(t => t.id === item.id);
      const currentStatus = originalTask?.status;

      // Если статус не изменился, не делаем ничего
      if (currentStatus === newStatus) {
        return;
      }

      // Проверяем права доступа для завершения задачи
      if (newStatus === 'closed') {
        const currentUserId = parseInt(localStorage.getItem('user_id'));
        const canComplete = currentUserId === originalTask?.creator_id ||
          currentUserId === originalTask?.assignee_id ||
          currentUserId === originalTask?.co_assignee_id;

        if (!canComplete) {
          message.warning('Только автор, исполнитель или соисполнитель могут завершать задачи');
          return;
        }

        // Если result_required = true, показываем модальное окно
        if (originalTask?.result_required) {
          setPendingTask({ item, newStatus, newIndex });
          setShowCompleteModal(true);
          return;
        }
      }

      // Проверяем, разрешен ли переход
      if (!isTransitionAllowed(currentStatus, newStatus)) {
        message.warning('Данный переход статуса не разрешен');
        return;
      }

      await onStatusChange(item, newStatus, newIndex);
    } catch (error) {
      // Обработка ошибки при обновлении статуса задачи
    } finally {
      setDraggedTask(null);
    }
  };

  const handleDragCancel = async (item) => {
    try {
      // Очищаем состояние перетаскивания при отмене
      setDraggedTask(null);
    } catch (error) {
      // Обработка ошибки при отмене перетаскивания
    }
  };



  const getStatusesWithBlocking = () => {
    const currentUserId = parseInt(localStorage.getItem('user_id'));

    const filteredStatuses = Object.keys(STATUSES).reduce((acc, key) => {
      if (selectedStatuses.includes(key)) {
        acc[key] = STATUSES[key];
      }
      return acc;
    }, {});

    // Если есть перетаскиваемая задача, блокируем недоступные колонки
    if (draggedTask) {
      const allowedStatuses = getAllowedStatuses(draggedTask.status);

      Object.keys(filteredStatuses).forEach(statusKey => {
        if (!allowedStatuses.includes(statusKey)) {
          filteredStatuses[statusKey] = {
            ...filteredStatuses[statusKey],
            disabled: true
          };
        }
      });

      // Проверяем права на завершение для перетаскиваемой задачи
      const canCompleteTask = currentUserId === draggedTask.creator_id ||
        currentUserId === draggedTask.assignee_id ||
        currentUserId === draggedTask.co_assignee_id;

      // Если пользователь не может завершить задачу, блокируем колонку "Закрыта"
      if (filteredStatuses.closed && !canCompleteTask) {
        filteredStatuses.closed = {
          ...filteredStatuses.closed,
          disabled: true,
          style: {
            filter: 'blur(2px)',
            opacity: 0.5,
            pointerEvents: 'none'
          }
        };
      }
    }

    // Блокируем колонку "Закрыта" для пользователей без прав (когда нет перетаскивания)
    if (filteredStatuses.closed && !draggedTask) {
      const canCompleteAny = tasks.some(task => {
        const currentUserId = parseInt(localStorage.getItem('user_id'));
        return currentUserId === task.creator_id ||
          currentUserId === task.assignee_id ||
          currentUserId === task.co_assignee_id;
      });

      if (!canCompleteAny) {
        filteredStatuses.closed = {
          ...filteredStatuses.closed,
          disabled: true
        };
      }
    }

    return filteredStatuses;
  };

  return (
    <>
      <Kanban
        items={tasks}
        columns={getStatusesWithBlocking()}
        renderCard={renderCard}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        isTransitionAllowed={isTransitionAllowed}
      />
      <TaskDetails
        visible={detailsVisible}
        onClose={() => setDetailsVisible(false)}
        task={selectedTask}
        candidates={candidates}
        users={users}
      />

      <Modal
        title="Завершение задачи"
        open={showCompleteModal}
        onCancel={handleCompleteTaskCancel}
        onOk={handleCompleteTaskSubmit}
        okText="Завершить"
        cancelText="Отмена"
        okButtonProps={{
          disabled: pendingTask?.item?.result_required && !completeResult.trim(),
          style: pendingTask?.item?.result_required && !completeResult.trim()
            ? {
              backgroundColor: '#f5f5f5',
              borderColor: '#d9d9d9',
              color: '#bfbfbf',
              cursor: 'not-allowed'
            }
            : { backgroundColor: '#1890ff', borderColor: '#1890ff' }
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: 16, color: '#666' }}>
            Опишите результат выполнения задачи:
          </p>
          <TextArea
            rows={4}
            placeholder="Введите описание результата..."
            value={completeResult}
            onChange={(e) => setCompleteResult(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
};

export default TaskKanban;
